import React, { useState, useEffect } from 'react'
import Wrapper from '../../components/Wrapper'
import { useParams, useNavigate } from 'react-router-dom'
import { Paper, Box, Typography, IconButton, Alert, TextField, Grid, Button, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox, Tooltip } from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import InfoIcon from '@mui/icons-material/Info'
import { did, global, included, input, menu, sip, employee } from '../../translate'
import { toast } from 'react-toastify'
import { 
  greenTheme,
  inputStyles, 
  selectStyles, 
  inputLabelStyles, 
  sectionTitleStyles,
  buttonStyles,
  checkboxStyles,
  captionTypographyStyles
} from '../../helpers/style'
import Api from '../../Api'
import SelectReact from 'react-select'
import { customStyles } from '../../helpers/style'

export default function SipProvidersSingle() {
  const { id } = useParams()
  const navigate = useNavigate()
  const langIndex = +localStorage.getItem('atsLang') || 1
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    provider: '',
    username: '',
    password: '',
    notes: '',
    lines: 1,
    server_host: '',
    no_action: '',
    caller_visible_type: 0
  })
  const [shorts, setShorts] = useState([])
  const [providers, setProviders] = useState([])
  const [fixProviders, setFixProviders] = useState([])
  const [isEdit, setIsEdit] = useState(false)

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true)
      try {
        const [shortResponse, providerResponse] = await Promise.all([
          Api.shortForVirtual(),
          Api.getCreateDid()
        ])
        setShorts(shortResponse?.data?.short || [])
        setProviders(providerResponse?.data?.inputNumber?.provider || [])
        const fixProvidersList = providerResponse?.data?.inputNumber?.fix_provider || []
        setFixProviders(fixProvidersList)

        if (id && id !== 'add') {
          setIsEdit(true)
          try {
            const { data } = await Api.getSingleProvider(id)
            if (data?.data) {
              const providerData = data.data
              const providerMatch = fixProvidersList.find(p => p.title === providerData.name)
              setForm({
                id: providerData.id,
                provider: providerMatch?.name || providerData.provider || '',
                username: providerData.username || '',
                password: providerData.password || '',
                notes: providerData.notes || '',
                lines: providerData.lines || 1,
                server_host: providerData.server_host || '',
                no_action: providerData.no_action || '',
                caller_visible_type: providerData.caller_visible_type || 0
              })
            }
          } catch (error) {
            console.error('Error fetching SIP provider:', error)
            toast.error('Error loading SIP provider data')
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error)
        toast.error('Error loading data')
      } finally {
        setLoading(false)
      }
    }

    fetchInitialData()
  }, [id])

  const handleChange = (name, value) => {
    setForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleWheel = (event) => {
    event.target.blur()
    event.preventDefault()
  }

  const handleGoBack = () => {
    navigate('/sip_providers')
  }

  const handleSave = async () => {
    if (!form.provider) {
      toast.error(did.operator[langIndex] + ' ' + (input.is_req?.[langIndex] || 'is required'))
      return
    }
    const nameTrim = String(form.notes || '').trim()
    if (!nameTrim) {
      toast.error(sip.note[langIndex] + ' ' + (input.is_req?.[langIndex] || 'is required'))
      return
    }
    if (!form.username) {
      toast.error(included.phone[langIndex] + ' ' + (input.is_req?.[langIndex] || 'is required'))
      return
    }

    setSaving(true)
    try {
      if (isEdit) {
        await Api.editProvider(form, 100, 1)
        toast.success(global.edit[langIndex] + ' ' + global.success[langIndex])
        navigate('/sip_providers')
      } else {
        await Api.createProvider(form, 100, 1)
        toast.success(sip.add_btn[langIndex] + ' ' + global.success[langIndex])
        navigate('/sip_providers')
      }
    } catch (error) {
      console.error('Error saving SIP provider:', error)
      toast.error(isEdit ? 'Failed to save changes' : 'Failed to create SIP provider')
    } finally {
      setSaving(false)
    }
  }

  const showServerHost = form.provider && (+form.provider === 27 || +form.provider === 42)

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <IconButton
              size="small"
              sx={{ color: greenTheme.primary, fontSize: 18 }}
              onClick={handleGoBack}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              {menu.provide[langIndex]}
            </Typography>
          </Box>

          <Alert icon={<InfoIcon />} severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {did.sip_provider_info[langIndex]}
            </Typography>
          </Alert>

          <Paper sx={{ p: 3, border: `1px solid ${greenTheme.primary}` }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={sectionTitleStyles}>
                {did.sip[langIndex]}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" sx={selectStyles} required>
                    <InputLabel sx={inputLabelStyles}>{did.operator[langIndex]}</InputLabel>
                    <Select
                      value={form.provider}
                      onChange={(e) => handleChange('provider', e.target.value)}
                      disabled={isEdit}
                      label={did.operator[langIndex]}
                    >
                      <MenuItem value="">{input.select[langIndex]}</MenuItem>
                      {fixProviders.map(p => (
                        <MenuItem key={p.name} value={p.name}>{p.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {showServerHost && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={did.server_host[langIndex]}
                      placeholder={did.server_host[langIndex]}
                      value={form.server_host}
                      onChange={(e) => handleChange('server_host', e.target.value)}
                      variant="outlined"
                      sx={inputStyles}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={sip.note[langIndex]}
                    placeholder={sip.note[langIndex]}
                    value={form.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    variant="outlined"
                    required
                    sx={inputStyles}
                  />
                </Grid>

                {isEdit && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Tooltip title={did.visibleExplain[langIndex]} arrow>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!form.caller_visible_type}
                              onChange={(e) => handleChange('caller_visible_type', e.target.checked ? 1 : 0)}
                              sx={checkboxStyles}
                            />
                          }
                          label={did.visible[langIndex]}
                        />
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" sx={captionTypographyStyles}>
                          {did.no_answer[langIndex]}
                        </Typography>
                        <SelectReact
                          styles={customStyles}
                          menuPlacement="auto"
                          menuPosition="fixed"
                          onChange={(option) => handleChange('no_action', option ? option.value : '')}
                          menuPortalTarget={document.body}
                          isSearchable={true}
                          value={shorts.find(s => s.virtual?.toString() === form.no_action?.toString()) 
                            ? { 
                                value: +form.no_action, 
                                label: `${form.no_action} (${shorts.find(s => +s.virtual === +form.no_action)?.name || ''})` 
                              } 
                            : null}
                          placeholder={input.select[langIndex]}
                          options={[
                            { value: '', label: input.select[langIndex] },
                            ...shorts.map(s => ({ 
                              value: s.virtual, 
                              label: `${s.virtual} (${s.name || ''})` 
                            }))
                          ]}
                        />
                      </Box>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={included.phone[langIndex]}
                    placeholder={included.phone[langIndex]}
                    value={form.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    required
                    sx={inputStyles}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type={isEdit ? 'password' : 'text'}
                    label={sip.pass[langIndex]}
                    placeholder={sip.pass[langIndex]}
                    value={form.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    variant="outlined"
                    sx={inputStyles}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={did.line[langIndex]}
                    placeholder={did.line[langIndex]}
                    value={form.lines}
                    onChange={(e) => handleChange('lines', e.target.value)}
                    onWheel={handleWheel}
                    variant="outlined"
                    sx={inputStyles}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={saving || loading}
                sx={buttonStyles}
              >
                {saving ? (isEdit ? global.edit[langIndex] + '...' : sip.add_btn[langIndex] + '...') : (isEdit ? global.edit[langIndex] : sip.add_btn[langIndex])}
              </Button>
            </Box>
          </Paper>
        </Paper>
      </div>
    </Wrapper>
  )
}
